import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowContainer,
  Container,
  LeftContent,
  RightContent,
  RightContentOverlay,
  StyledVideo,
  StyledImage,
  UploadImageContainer,
  UploadImageButton,
  DeleteButton,
  NudifyText,
  CheckboxContainer,
  GenerateContainer,
  GenerateButton,
  IconWrapper,
  TooltipContainer,
  TooltipText,
  InfoIconWrapper,
  ContainerWrapper,
  GenerateContainerMobile,
} from "./ImageToVideo.styles";
import { ReactComponent as ArrowIcon } from "../../assets/icons/arrow.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/delete.svg";
import {
  selectGenerateCollection,
  selectRegenerateCollectionPending,
  selectSelectedCollection,
} from "../../store/slices/model/slice";
import { ReactComponent as CheckboxUncheckedIcon } from "../../assets/icons/checkbox-unchecked.svg";
import { ReactComponent as CheckboxCheckedIcon } from "../../assets/icons/checkbox-checked.svg";
import { ReactComponent as SettingsIcon } from "../../assets/icons/settings.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info-circle.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  DEFAULT_VIDEO_FPS,
  DEFAULT_VIDEO_RESOLUTION,
  MAX_IMAGE_SIZE,
  MEDIA_TYPE,
  NUDIFY_MODEL_NAME,
  NUDIFY_PRICE,
  REGENERATE_DISCOUNT_PERCENTAGE,
  VIDEO_RESOLUTION_PRICE,
} from "../../utils/constants";
import { toast } from "sonner";
import EnterPromptModal from "../EnterPromptModal/EnterPromptModal";
import {
  generateCollection,
  regenerateCollection,
} from "../../store/slices/model/asyncThunks";
import { useNavigate } from "react-router-dom";
import GradientStarIcon from "../GradientStarIcon/GradientStarIcon";
import heic2any from "heic2any";
import { useMediaQuery } from "react-responsive";

const ImageToVideo = ({ onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef();
  const collection = useSelector(selectSelectedCollection);
  const { loading: generateLoading } = useSelector(selectGenerateCollection);
  const regenerateLoading = useSelector(selectRegenerateCollectionPending);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [nudifyEnabled, setNudifyEnabled] = useState(false);
  const [isEnterPromptModalOpen, setIsEnterPromptModalOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [customSeed, setCustomSeed] = useState(
    Math.floor(Math.random() * 10000000) + 1
  );
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [videoResolution, setVideoResolution] = useState(
    collection?.regenerate
      ? collection?.output.resolution ?? DEFAULT_VIDEO_RESOLUTION
      : localStorage.getItem("videoResolution") ?? DEFAULT_VIDEO_RESOLUTION
  );

  const [fps, setFps] = useState(
    collection?.regenerate
      ? collection?.output.fps ?? DEFAULT_VIDEO_FPS
      : Number(localStorage.getItem("videoFps")) ?? DEFAULT_VIDEO_FPS
  );

  const [editedVideoFps, setEditedVideoFps] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [editedSetup, setEditedSetup] = useState(false);
  const [editedSource, setEditedSource] = useState(false);
  const [editedResolution, setEditedResolution] = useState(false);

  const isImageToVideoRegenerate = useMemo(
    () => collection?.regenerate && !collection?.input?.video?.url,
    [collection]
  );

  const collectionPrice = useMemo(() => {
    if (
      !editedSetup &&
      !editedSource &&
      isImageToVideoRegenerate &&
      !editedResolution &&
      !editedVideoFps
    ) {
      return (
        (collection?.price +
          (collection.nudify ? NUDIFY_PRICE : 0) +
          VIDEO_RESOLUTION_PRICE[videoResolution]) *
        (1 - REGENERATE_DISCOUNT_PERCENTAGE)
      );
    } else {
      return (
        collection?.price +
        (nudifyEnabled ? NUDIFY_PRICE : 0) +
        VIDEO_RESOLUTION_PRICE[videoResolution]
      );
    }
  }, [
    editedSetup,
    editedSource,
    isImageToVideoRegenerate,
    editedResolution,
    editedVideoFps,
    collection?.price,
    collection.nudify,
    nudifyEnabled,
    videoResolution,
  ]);

  useEffect(() => {
    if (collection) {
      if (collection.nudify) {
        setNudifyEnabled(true);
      }
    }
  }, [collection]);

  if (!collection) {
    return null;
  }

  const output = collection.output;
  const input = collection.input;

  const handleFileChange = async (e) => {
    const { files } = e.target;

    let file = files[0];

    if (
      file &&
      [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
        "image/heic",
        "image/heif",
      ].includes(file.type)
    ) {
      if (file.size > MAX_IMAGE_SIZE) {
        toast.error("Image must be smaller than 7MB");
        return;
      }

      let blobToShow = file;

      if (file.type === "image/heic" || file.type === "image/heif") {
        try {
          const convertedBlob = await heic2any({
            blob: file,
            toType: "image/jpg",
            quality: 0.9,
          });
          blobToShow = convertedBlob;

          file = new File(
            [convertedBlob],
            file.name.replace(/\.[^/.]+$/, ".jpg"),
            {
              type: "image/jpg",
              lastModified: Date.now(),
            }
          );
        } catch (err) {
          console.error("HEIC → jpg conversion failed", err);
          return;
        }
      }

      setUploadedImage({ file, url: URL.createObjectURL(blobToShow) });
      if (isImageToVideoRegenerate) {
        setEditedSource(true);
      }
    } else {
      toast.error("Unsupported media type.");
    }
  };

  const onGenerateClick = () => {
    if ((localStorage.getItem("wasPromptModalOpen") ?? false) === false) {
      setIsEnterPromptModalOpen(true);
      localStorage.setItem("wasPromptModalOpen", true);
      return;
    }

    if (
      !editedSetup &&
      !editedSource &&
      isImageToVideoRegenerate &&
      !editedResolution &&
      !editedVideoFps
    ) {
      dispatch(
        regenerateCollection({
          collectionId: collection.collectionId,
          seed: customSeed,
        })
      )
        .unwrap()
        .then(() => {
          toast.success("Regeneration started...");
          navigate("/collection");
          setTimeout(() => {
            window.scrollTo(0, 0);
          }, 300);

          setUploadedImage(null);
          setCustomPrompt("");
          setNudifyEnabled(false);
          setIsEnterPromptModalOpen(false);
          onClose();
        })
        .catch((err) => {
          toast.error(err);
        });
    } else {
      dispatch(
        generateCollection({
          image: uploadedImage,
          customPrompt,
          nudify: nudifyEnabled,
          modelId: collection.model,
          resolution: videoResolution,
          seed: customSeed,
          fps,
        })
      )
        .unwrap()
        .then(() => {
          toast.success("Generation started...");
          navigate("/collection");
          setTimeout(() => {
            window.scrollTo(0, 0);
          }, 300);
          setUploadedImage(null);
          setCustomPrompt("");
          setNudifyEnabled(false);
          setIsEnterPromptModalOpen(false);
          onClose();
        })
        .catch((err) => {
          toast.error(err);
        });
    }
  };

  const renderGenerateContainer = () => {
    return (
      <>
        <IconWrapper
          onClick={() => {
            setIsEnterPromptModalOpen(true);
            localStorage.setItem("wasPromptModalOpen", true);
          }}
        >
          <SettingsIcon />
        </IconWrapper>
        <GenerateButton
          disabled={generateLoading || regenerateLoading}
          onClick={onGenerateClick}
        >
          {generateLoading || regenerateLoading ? (
            <l-line-spinner size="16" stroke="2" speed="1" color="#000" />
          ) : (
            <>
              {!editedSetup &&
              !editedSource &&
              isImageToVideoRegenerate &&
              !editedResolution &&
              !editedVideoFps
                ? "Regenerate"
                : "Generate"}
              <GradientStarIcon />
              {Math.ceil(collectionPrice)}
            </>
          )}
        </GenerateButton>
        <InfoIconWrapper
          onMouseEnter={() => {
            if (!isMobile) {
              setShowTooltip(true);
            }
          }}
          onMouseLeave={() => {
            if (!isMobile) {
              setShowTooltip(false);
            }
          }}
          onClick={() => {
            if (isMobile) {
              setShowTooltip(!showTooltip);
            }
          }}
        >
          <InfoIcon width={24} height={24} />
          {showTooltip && (
            <TooltipContainer>
              <TooltipText>
                The uploaded image will be used as the first frame of the
                generated video.
                <br /> For best results, match the original image. The AI
                follows basic physics - objects not in the input image are
                unlikely to appear in the generated video.
              </TooltipText>
            </TooltipContainer>
          )}
        </InfoIconWrapper>
      </>
    );
  };

  return (
    <ContainerWrapper>
      <Container>
        <LeftContent>
          <UploadImageContainer>
            {uploadedImage && (
              <DeleteButton
                onClick={() => {
                  setUploadedImage(null);
                  if (isImageToVideoRegenerate) {
                    setEditedSource(false);
                  }
                }}
              >
                <DeleteIcon />
              </DeleteButton>
            )}
            <StyledImage
              src={
                uploadedImage?.url ||
                input.image.resizedImage.url ||
                input.image.url
              }
            />
            <UploadImageButton onClick={() => fileInputRef.current?.click()}>
              Upload Image
            </UploadImageButton>
            <input
              type="file"
              ref={fileInputRef}
              accept=".jpg,.jpeg,.png,.webp,.heif,.heic"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </UploadImageContainer>
          {collection.modelName !== NUDIFY_MODEL_NAME && (
            <CheckboxContainer
              onClick={() => {
                setNudifyEnabled(!nudifyEnabled);
                if (isImageToVideoRegenerate) {
                  setEditedSetup(collection.nudify !== !nudifyEnabled);
                }
              }}
            >
              {nudifyEnabled ? (
                <CheckboxCheckedIcon color={"white"} />
              ) : (
                <CheckboxUncheckedIcon color={"white"} />
              )}
              <NudifyText>Nudify before scene</NudifyText>
            </CheckboxContainer>
          )}
          <GenerateContainer>{renderGenerateContainer()}</GenerateContainer>
        </LeftContent>
        <RightContent>
          {output.type === MEDIA_TYPE.VIDEO ? (
            <StyledVideo
              src={output.video.compressedUrl || output.video.url}
              imageSrc={input.image.resizedImage.url || input.image.url}
            />
          ) : (
            <StyledImage src={output.image.url} />
          )}
          <RightContentOverlay />
          {/* <ModelName>{formatModelName(collection.name)}</ModelName>
          <Username>@{collection.username}</Username> */}
        </RightContent>
        <ArrowContainer>
          <ArrowIcon />
        </ArrowContainer>
        <EnterPromptModal
          isOpen={isEnterPromptModalOpen}
          onClose={() => setIsEnterPromptModalOpen(false)}
          customPrompt={customPrompt}
          setCustomPrompt={setCustomPrompt}
          videoResolution={videoResolution}
          setVideoResolution={setVideoResolution}
          setEditedResolution={setEditedResolution}
          collectionVideoResolution={collection.output.resolution}
          enableVideoQuality={collection.modelName !== NUDIFY_MODEL_NAME}
          modelPrice={collection?.price + (nudifyEnabled ? NUDIFY_PRICE : 0)}
          regeneratePrice={
            (collection?.price +
              (collection.nudify ? NUDIFY_PRICE : 0) +
              VIDEO_RESOLUTION_PRICE[collection.output.resolution]) *
            (1 - REGENERATE_DISCOUNT_PERCENTAGE)
          }
          isEdited={editedSetup || editedSource || !isImageToVideoRegenerate}
          setCustomSeed={setCustomSeed}
          customSeed={customSeed}
          fps={fps}
          setFps={setFps}
          setEditedVideoFps={setEditedVideoFps}
          collectionVideoFps={collection.output.fps}
        />
      </Container>
      <GenerateContainerMobile>
        {renderGenerateContainer()}
      </GenerateContainerMobile>
    </ContainerWrapper>
  );
};

export default ImageToVideo;
